import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Input, Empty } from "../components/UI";
import { usePagination, PaginationBar } from "../components/Pagination";
import { PenLine, Search, RefreshCw, ExternalLink, PartyPopper, Lock, Check } from "lucide-react";
import { toast } from "sonner";

export default function OrderSignOff() {
  useEffect(() => { document.title = "Order Sign-Off | COCOA EHR"; }, []);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [search, setSearch] = useState("");
  const [armed, setArmed] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/orders/sign-off-queue").then((r) => { setData(r.data); setDenied(false); })
    .catch((e) => { if (e.response?.status === 403) setDenied(true); else toast.error(apiError(e)); });
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => {
    if (!data) return [];
    const qq = search.trim().toLowerCase();
    return data.items.filter((it) => !qq || `${it.template_name} ${it.client_name} ${it.author}`.toLowerCase().includes(qq));
  }, [data, search]);
  const pg = usePagination(shown);

  const signNow = async (it) => {
    setBusy(true);
    try {
      await api.post(`/clinical-forms/${it.id}/sign`);
      toast.success(`${it.template_name} signed for ${it.client_name}`);
      setArmed(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (denied) return (
    <Card className="mx-auto mt-10 max-w-md p-10 text-center" data-testid="order-signoff-denied">
      <Lock size={28} className="mx-auto mb-3 text-[#7B6157]" />
      <div className="font-heading text-lg font-bold text-[#3E2723]">Prescriber access required</div>
      <div className="text-sm text-[#7B6157]">The Order Sign-Off Queue is available to clinicians and administrators only.</div>
    </Card>
  );

  if (!data) return <div className="space-y-4"><div className="h-24 animate-pulse rounded-2xl bg-[#EFEBE9]" /><div className="h-64 animate-pulse rounded-2xl bg-[#EFEBE9]" /></div>;

  return (
    <div data-testid="order-signoff-page">
      <PageHeader title="Order Sign-Off Queue" subtitle={`${data.count} unsigned order${data.count === 1 ? "" : "s"} awaiting a prescriber signature`}
        action={<Btn variant="outline" data-testid="signoff-refresh" onClick={() => { load(); toast.success("Refreshed"); }}><RefreshCw size={14} /> Refresh</Btn>} />
      <Card className="p-4">
        <div className="relative mb-3 w-64">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
          <Input data-testid="order-signoff-search" className="h-10 pl-9" placeholder="Search orders…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {data.count === 0 ? (
          <div className="p-8 text-center" data-testid="order-signoff-empty">
            <PartyPopper size={32} className="mx-auto mb-2 text-[#2E7D32]" />
            <div className="font-heading text-lg font-bold text-[#3E2723]">No unsigned orders</div>
            <div className="text-sm text-[#7B6157]">Every medication and order document is signed. Nothing is waiting on a prescriber.</div>
          </div>
        ) : shown.length === 0 ? <Empty text="No orders match your search" /> : (
          <div className="space-y-2">
            {pg.pageRows.map((it) => (
              <div key={it.id} data-testid={`signoff-row-${it.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-3">
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${it.days > 7 ? "bg-[#FFEBEE] text-[#C62828]" : "bg-[#FFF8E1] text-[#876600]"}`}>
                  {it.days === 0 ? "Today" : `${it.days}d pending`}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-[#3E2723]">{it.template_name}</div>
                  <div className="text-xs text-[#7B6157]">{it.client_name} · authored by {it.author}{it.date ? ` · ${dayjs(it.date).format("MMM D, YYYY")}` : ""}</div>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <button data-testid={`signoff-review-${it.id}`} onClick={() => navigate(it.link)}
                    className="flex items-center gap-1 rounded-full border border-[#E5DFD3] px-3 py-1.5 text-xs font-bold text-[#6D4C41] hover:bg-[#F5F2EB]"><ExternalLink size={12} /> Review</button>
                  {armed === it.id ? (
                    <button data-testid={`signoff-confirm-${it.id}`} disabled={busy} onClick={() => signNow(it)}
                      className="flex items-center gap-1 rounded-full bg-[#C62828] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#B71C1C] disabled:opacity-60"><Check size={12} /> Confirm sign</button>
                  ) : (
                    <button data-testid={`signoff-sign-${it.id}`} onClick={() => setArmed(it.id)}
                      className="flex items-center gap-1 rounded-full bg-[#2E7D32] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#1B5E20]"><PenLine size={12} /> Sign now</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <PaginationBar {...pg} testId="signoff-pagination" />
      </Card>
    </div>
  );
}
